import { getConfig } from "@/features/branding";
import { AssignedColumn } from "@/features/csv_setup_table";
import { geocodeAddress, HubGeocoderKey, HubGeocoderResult } from "@/features/geocoders";
import { LatLngLiteral } from "leaflet";
import { startCase, toLower } from "lodash";

export const GEOCODING_ERRORS_FROM_API = [
  "Geocode quality was not good enough to continue analysis",
  "Could not determine coordinates for given address",
];

interface GeocodeAddressesResponse {
  results: HubGeocoderResult[];
  unmatched: string[];
}

// If a valid coordinate string, converts to LatLng, else returns null.
export const convertStringToLatLng = (input: string): LatLngLiteral | null => {
  const segments = input.split(",");
  if (segments.length !== 2) {
    return null;
  }

  const lat = parseFloat(segments[0].trim());
  const lng = parseFloat(segments[1].trim());

  return isNaN(lat) || isNaN(lng) ? null : { lat, lng };
};

export const extractAddressesFromColumnsAndData = (columns: AssignedColumn[], data: string[][]): string[] => {
  const addressIndex = columns.findIndex(({ option }) => option.value === "address");
  return data.slice(1).map((row) => row[addressIndex]);
};

// Geocode all addresses via given geocoders.
export const geocodeAddresses = async (
  addresses: string[],
  geocoders: HubGeocoderKey[] = getConfig().geocoding.defaultOrder as any
): Promise<GeocodeAddressesResponse> => {
  const results = await Promise.all(
    addresses.map((address, index) => {
      return new Promise<HubGeocoderResult>((res) => {
        window.setTimeout(async () => {
          res((await geocodeAddress(address, geocoders)) as HubGeocoderResult);
        }, 250 * index);
      });
    })
  );

  return results.reduce<GeocodeAddressesResponse>(
    (obj, result, index) => {
      return result
        ? { ...obj, results: [...obj.results, result] }
        : { ...obj, unmatched: [...obj.unmatched, addresses[index]] };
    },
    {
      results: [],
      unmatched: [],
    }
  );
};

export const limitLatLngTo6dp = (latLng: LatLngLiteral) => {
  return {
    lat: latLng.lat.toFixed(6),
    lng: latLng.lng.toFixed(6),
  };
};

export const concatLatLng = (latLng: LatLngLiteral) => {
  const { lat, lng } = limitLatLngTo6dp(latLng);
  return `${lat}_${lng}`;
};

/**
 * Evaluates whether or not a given latitude is valid.
 * @param lat - Latitude to validate.
 * @returns Validity of latitude.
 */
export const validateLatitude = (lat: number): boolean => {
  const invalid = isNaN(lat) || lat < -90 || lat > 90;
  return !invalid;
};

/**
 * Evaluates whether or not a given longitude is valid.
 * @param lng - Longitude to validate.
 * @returns Validity of longitude.
 */
export const validateLongitude = (lng: number): boolean => {
  const invalid = isNaN(lng) || lng < -180 || lng > 180;
  return !invalid;
};
/**
 * processes geocode quality returned by api based on if lat/long was provided
 * @param quality - quality returned by api, empty if geocoding was skipped.
 * @param latitude - latitude, if lat/long was provided, geocoding was skipped.
 * @param longitude - longitude, if lat/long was provided, geocoding was skipped.
 * @returns geocode quality including 'provided' were applicable
 */
export const postProcessGeocodeQuality = ({
  quality = "",
  latitude = "",
  longitude = "",
}: {
  quality: string | undefined | null;
  latitude: string | number | undefined | null;
  longitude: string | number | undefined | null;
}) => {
  const coordinatesProvided = latitude != null && longitude != null;
  if (coordinatesProvided && (!quality || quality.toLowerCase() === "unknown")) {
    return "Provided";
  }
  return startCase(toLower(quality || "unknown"));
};
